import posthog from "posthog-js";

/**
 * Analytics + error tracking for the public site (PostHog — replaced Sentry).
 *
 * PRIVACY INVARIANT: events carry structural metadata only — profile key,
 * step id, path, field NAMES. Never a field value the customer typed.
 * Session recording masks all text/inputs for the same reason.
 *
 * One PostHog project holds every Company's form and the admin app beside
 * them, so every event says which app sent it and, once the profile has
 * loaded, whose form it is (`company`, the Company's slug). A profile key
 * is a Discipline and several Companies share one, so it cannot.
 */

declare const __POSTHOG_KEY__: string | undefined;

let enabled = false;

export function initAnalytics(): void {
  if (typeof __POSTHOG_KEY__ === "undefined" || !__POSTHOG_KEY__) return;
  posthog.init(__POSTHOG_KEY__, {
    api_host: "https://us.i.posthog.com",
    autocapture: false,
    capture_pageview: true,
    // Unhandled errors / rejections → PostHog error tracking.
    capture_exceptions: true,
    // Recording is off globally; the inquiry funnel switches it on for its
    // duration (InquiryReplayBoundary) — that is where the "do clients
    // complete the configurator unaided" question lives. All text and
    // inputs are masked so PII the client types is never sent.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
    },
    respect_dnt: true,
  });
  posthog.register({ app: "form" });
  enabled = true;
}

/** Name the Company whose form this is on every event from here on. */
export function setAnalyticsCompany(slug: string | undefined): void {
  if (!enabled || !slug) return;
  posthog.register({ company: slug });
}

/** Full-session recording scoped to the inquiry funnel. */
export function startFunnelRecording(): void {
  if (!enabled) return;
  posthog.startSessionRecording();
}

export function stopFunnelRecording(): void {
  if (!enabled) return;
  posthog.stopSessionRecording();
}

export type AnalyticsEvent =
  | "path_chosen"
  | "step_viewed"
  | "step_completed"
  | "field_error_shown"
  | "tech_rider_uploaded"
  | "tech_rider_parsed"
  | "tech_rider_parse_failed"
  | "inquiry_submitted"
  | "inquiry_submit_failed"
  | "draft_restored"
  | "quote_ready_shown"
  | "estimate_shown"
  | "estimate_review_shown"
  | "estimate_review_continued"
  | "estimate_review_abandoned";

export interface AnalyticsProps {
  profileKey?: string;
  step?: string;
  path?: string;
  fieldNames?: readonly string[];
  /** The key the browser minted for one submission, stored on the
   *  Inquiry as `submission_key`: what joins a form session to what the
   *  Inquiry became. Minted, never typed, so it is not the Customer's. */
  submissionKey?: string;
}

export function track(event: AnalyticsEvent, props: AnalyticsProps = {}): void {
  if (!enabled) return;
  posthog.capture(event, props);
}
